import "../styles/tokens.css";
import "../styles/base.css";
import "../styles/components.css";
import "../styles/responsive.css";

import { localeFromPath, localePath, messages } from "../i18n";
import type { Locale } from "../i18n/types";
import { collectBrowserEnvironment } from "./device-detection";
import { renderApp, type AppState } from "./renderer";
import { fetchRuntimeConfig, requestScan, ScanApiError } from "./scan-controller";
import { updateDocumentMetadata } from "./seo";
import { saveLocale } from "./storage";
import { loadTurnstile } from "./turnstile";
import { observeWebRtcPublicIps } from "./webrtc-detection";

const root = document.querySelector<HTMLElement>("#app");
if (!root) throw new Error("Missing #app root");
const appRoot: HTMLElement = root;

let turnstileToken: string | undefined;
const initialLocale = localeFromPath(location.pathname) ?? "zh-TW";
const state: AppState = {
  locale: initialLocale,
  phase: "idle",
  browser: null,
  scan: null,
  webrtc: null,
  ping: null,
  error: null,
  runtimeConfig: { turnstileEnabled: false },
  hasScanned: false
};

function localizedError(error: unknown): string {
  const copy = messages[state.locale];
  if (error instanceof ScanApiError) {
    if (error.status === 429) return copy.errors.rateLimited;
    if (error.code.includes("turnstile")) return copy.errors.turnstile;
    if (error.status === 400 || error.status === 413) return copy.errors.invalid;
  }
  return copy.errors.generic;
}

function draw(): void {
  const copy = messages[state.locale];
  renderApp(appRoot, state, copy, { onScan: () => void runScan(), onLocale: switchLocale });
  updateDocumentMetadata(state.locale, copy);
}

function switchLocale(locale: Locale): void {
  if (locale === state.locale) return;
  state.locale = locale;
  saveLocale(locale);
  history.pushState({ locale }, "", localePath(locale));
  draw();
}

async function runScan(): Promise<void> {
  const copy = messages[state.locale];
  if (state.runtimeConfig.turnstileEnabled && !turnstileToken) {
    state.error = copy.errors.turnstile;
    draw();
    return;
  }
  if (state.hasScanned) {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  state.hasScanned = true;
  state.error = null;
  state.scan = null;
  state.webrtc = null;
  state.ping = null;
  state.browser = collectBrowserEnvironment();
  state.phase = "local";
  draw();

  const webrtcPromise = observeWebRtcPublicIps();
  state.phase = "network";
  draw();

  const [scanResult, webrtcResult] = await Promise.allSettled([
    requestScan(turnstileToken),
    webrtcPromise
  ]);

  if (scanResult.status === "fulfilled") state.scan = scanResult.value;
  else state.error = localizedError(scanResult.reason);

  if (webrtcResult.status === "fulfilled") {
    state.webrtc = webrtcResult.value;
    if (state.scan)
      state.webrtc.additionalPublicIps = state.webrtc.additionalPublicIps.filter(
        (ip) => ip !== state.scan?.network.ip
      );
  }


  state.phase = state.scan || state.browser ? "complete" : "error";
  turnstileToken = undefined;
  window.turnstile?.reset();
  draw();
}

window.addEventListener("popstate", () => {
  state.locale = localeFromPath(location.pathname) ?? state.locale;
  draw();
});

let autoScanTriggered = false;
function triggerAutoScan(): void {
  if (autoScanTriggered) return;
  autoScanTriggered = true;
  void runScan();
}

draw();
void fetchRuntimeConfig().then((config) => {
  state.runtimeConfig = config;
  draw();
  if (config.turnstileEnabled && config.turnstileSiteKey) {
    void loadTurnstile((token) => {
      const isFirstToken = !turnstileToken;
      turnstileToken = token;
      state.error = null;
      if (isFirstToken) {
        triggerAutoScan();
      }
    });
  } else {
    triggerAutoScan();
  }
});
